import React from 'react'
import PropTypes from 'prop-types'
import { Form } from 'antd'

import NextButton, { BackButton } from '../../common/NextButton'
import css from '../../common/Form.css'
import OpenchairUrl from './OpenchairUrl'
import PbxForm from './PbxForm'
import PatientPortalSubdirectory from './PatientPortalSubdirectory'
import PmsSelector from './PmsSelector'
import ProductPicker from './ProductPicker'
import RemindersMessageReports from './RemindersMessageReports'
import TierSelector from './TierSelector'
import CrmAdAccounts from './CrmAdAccounts'
import { productConstants } from '../../common/constants'

const Products = ({
  handleNamedInputChange,
  validateEmail,
  isTopsAdmin,
  messageReportEmail,
  additionalRecipients,
  onBack,
  onNext,
  updatePmsInState,
  onProductSelect,
  pbxDomain,
  pbxNumber,
  pbxServiceType,
  pbxUsername,
  pbxVEmail,
  pms,
  products,
  subdirectory,
  tier,
  unselectIneligibleProducts,
  website,
  adAccounts,
  adAccountIdsForPractice,
}) => {
  const dso = products && products.indexOf('dso') > -1
  const openchair = products && products.indexOf('openchair') > -1
  const patientportal = products && products.indexOf('patientportal') > -1
  const reminders = products && products.indexOf('reminders') > -1
  const reminderstier2 = products && products.indexOf('reminderstier2') > -1
  const topsremindersplus =
    products && products.indexOf('topsremindersplus') > -1
  // const topsalertsplus = products && products.indexOf('topsalertsplus') > -1
  const reviews = products && products.indexOf('reviews') > -1
  const textingonly = products && products.indexOf('textingonly') > -1
  const voip = products && products.indexOf('voip') > -1
  const crm = products && products.indexOf('crm') > -1
  const validateVoip = () => {
    if (!voip) return true
    const pbxVEmailCheck = !pbxVEmail ? true : validateEmail(pbxVEmail)
    if (pbxServiceType === 'coredial') {
      const pbxNumberCheck = pbxNumber && pbxNumber.length === 5
      const pbxUsernameCheck = pbxUsername && pbxUsername.trim()
      return pbxNumberCheck && pbxUsernameCheck && pbxVEmailCheck
    }
    if (pbxServiceType === 'skyswitch') {
      const pbxDomainCheck = pbxDomain && pbxDomain.includes('.23815.service') && pbxDomain !== '.23815.service'
      return pbxDomainCheck && pbxVEmailCheck
    }
    return false
  }
  const validateOsUrl = () => {
    if (!openchair) return true
    // Regex to match domain or IP with optional port, without paths beyond the domain/IP
    const urlPattern = '^(https?:\\/\\/)?((localhost)|([0-9]{1,3}(\\.[0-9]{1,3}){3})|([a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}))(:[0-9]+)?\\/?$'
    const urlRegex = new RegExp(urlPattern)
    return website && urlRegex.test(website)
  }
  const disableNext = !products || !products.length || !tier || !validateVoip() || !validateOsUrl()
  const checkboxDefaultProps = {
    onChange: onProductSelect,
  }

  const checkboxes = productConstants(isTopsAdmin)

  const pmsSpecificProducts = (selectedPms) => {
    const newPms = selectedPms || pms
    return checkboxes.filter(checkbox => checkbox.pmsEligible[newPms]).map(product => product.value)
  }

  const handlePmsSelect = ({ target: { value } }) => {
    updatePmsInState(value, () => {
      const productsToFilter = pmsSpecificProducts(value || 'none')
      unselectIneligibleProducts(productsToFilter)
    })
  }

  const productCbxFuncs = {
    dso: () => dso,
    openchair: () => openchair,
    patientportal: () => patientportal,
    reminders: () => reminders,
    reminderstier2: () => reminderstier2,
    topsremindersplus: () => topsremindersplus,
    reviews: () => reviews,
    voip: () => voip,
    textingonly: () => textingonly,
    crm: () => crm,
  }

  return (
    <Form
      layout="horizontal"
      onSubmit={e => {
        e.preventDefault()
        onNext()
      }}
    >
      {!isTopsAdmin && (
        <PmsSelector onChange={handlePmsSelect} value={pms} />
      )}
      <Form.Item label="Products" className={css.itemLabel} help={!isTopsAdmin ? 'Note: CRM is not compatible with DSO' : ''}>
        {checkboxes.map(cbx => (
          <ProductPicker
            {...checkboxDefaultProps}
            key={cbx.label}
            checked={productCbxFuncs[cbx.value]()}
            label={cbx.label}
            value={cbx.value}
            enabled={cbx.pmsEligible[pms || 'none']}
          />
        ))}
      </Form.Item>
      <TierSelector
        onChange={handleNamedInputChange}
        value={tier}
      />
      {reminders && (
        <RemindersMessageReports
          onChange={handleNamedInputChange}
          messageReportEmail={messageReportEmail}
          additionalRecipients={additionalRecipients}
        />
      )}
      {openchair && (
        <OpenchairUrl onChange={handleNamedInputChange} value={website} />
      )}
      {patientportal && !isTopsAdmin && pms !== 'topsOrtho' && (
        <PatientPortalSubdirectory
          onChange={handleNamedInputChange}
          value={subdirectory}
        />
      )}
      {voip && !isTopsAdmin && (
        <PbxForm
          onChange={handleNamedInputChange}
          pbxDomain={pbxDomain}
          pbxNumber={pbxNumber}
          pbxServiceType={pbxServiceType}
          pbxUsername={pbxUsername}
          pbxVEmail={pbxVEmail}
        />
      )}
      {crm && (
        <CrmAdAccounts
          onChange={handleNamedInputChange}
          value={adAccountIdsForPractice}
          adAccounts={adAccounts}
        />
      )}
      <Form.Item className={css.itemLabel}>
        <BackButton handleClick={onBack} />
        <NextButton isSubmit isDisabled={disableNext} />
      </Form.Item>
    </Form>
  )
}

Products.propTypes = {
  handleNamedInputChange: PropTypes.func.isRequired,
  validateEmail: PropTypes.func.isRequired,
  isTopsAdmin: PropTypes.bool.isRequired,
  messageReportEmail: PropTypes.string,
  additionalRecipients: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onProductSelect: PropTypes.func.isRequired,
  updatePmsInState: PropTypes.func.isRequired,
  unselectIneligibleProducts: PropTypes.func.isRequired,
  pms: PropTypes.oneOf([
    'topsOrtho',
    'Dolphin',
    'Cloud9',
    'OrthoTrac',
    'OrthoTracV2',
    'OpenDental',
    true,
  ]),
  products: PropTypes.arrayOf(
    PropTypes.oneOf([
      'dso',
      'reminders',
      'openchair',
      'voip',
      'patientportal',
      'reminderstier2',
      'reviews',
      'topsremindersplus',
      'topsalertsplus',
      'textingonly',
      'crm',
    ]),
  ).isRequired,
  subdirectory: PropTypes.string,
  tier: PropTypes.oneOf([1, 2, 3, 4]),
  website: PropTypes.string,
  pbxDomain: PropTypes.string,
  pbxNumber: PropTypes.string,
  pbxServiceType: PropTypes.string,
  pbxUsername: PropTypes.string,
  pbxVEmail: PropTypes.string,
  adAccounts: PropTypes.arrayOf(PropTypes.object),
  adAccountIdsForPractice: PropTypes.arrayOf(PropTypes.number),
}

Products.defaultProps = {
  additionalRecipients: null,
  messageReportEmail: null,
  pms: null,
  practiceId: null,
  subdirectory: null,
  tier: null,
  website: null,
  pbxDomain: null,
  pbxNumber: null,
  pbxServiceType: null,
  pbxUsername: null,
  pbxVEmail: null,
  adAccounts: [],
  adAccountIdsForPractice: [],
}

export default Products



// WEBPACK FOOTER //
// ./src/AddPractice/Products/index.js